import { Order } from './orders';

/**
 * Clientseitige Absicherung: verhindert, dass ein Save überhaupt losgeschickt wird,
 * wenn er Felder leert, die lokal noch Daten enthalten.
 *
 * Das Backend hat dieselbe Prüfung (guardOrderFields) als letztes Netz. Diese hier
 * greift eine Stufe früher und ist deshalb wichtig: Ein Request, der nie rausgeht,
 * kann auch nicht verspätet oder in falscher Reihenfolge auf dem Server landen —
 * genau das Muster, das in Produktion zum Verlust ganzer Produktlisten geführt hat.
 *
 * Bewusstes Leeren aller Produkte erfolgt manuell am Datensatz, nicht über die App.
 */

/** Felder, die niemals versehentlich geleert werden dürfen. */
export const PROTECTED_ORDER_FIELDS = [
  'products',
  'customerBase',
  'customerAddress',
  'customerEmail',
] as const;

const isEmptyValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'string') return value.trim() === '';
  if (typeof value === 'object') return Object.keys(value as object).length === 0;
  return false;
};

export type PayloadRepairResult = {
  /** Der zu sendende Auftrag — geleerte Felder sind durch den lokalen Stand ersetzt. */
  order: Order;
  /** Felder, die repariert wurden. Leer, wenn der Payload in Ordnung war. */
  repairedFields: string[];
};

/**
 * Ersetzt leere Felder im abzusendenden Auftrag durch die Werte aus dem lokal
 * bekannten Stand. Gibt es lokal auch nichts, bleibt das Feld leer — dann ist der
 * Auftrag tatsächlich leer und es geht nichts verloren.
 *
 * @param outgoing Der Auftrag, wie er gesendet werden soll.
 * @param known    Der zuletzt lokal gespeicherte Stand desselben Auftrags.
 */
export const repairOutgoingOrder = (
  outgoing: Order,
  known: Order | undefined,
  clearedFields: string[] = [],
): PayloadRepairResult => {
  if (!known) {
    return { order: outgoing, repairedFields: [] };
  }

  const clearedByUser = new Set(clearedFields);
  const knownRecord = known as unknown as Record<string, unknown>;
  const repaired: Record<string, unknown> = { ...(outgoing as unknown as Record<string, unknown>) };
  const repairedFields: string[] = [];

  for (const field of PROTECTED_ORDER_FIELDS) {
    // Bewusst geleerte Felder nicht wiederherstellen — die Löschung ist gewollt.
    if (clearedByUser.has(field)) continue;

    const outgoingValue = repaired[field];
    const knownValue = knownRecord[field];

    if (isEmptyValue(outgoingValue) && !isEmptyValue(knownValue)) {
      repaired[field] = knownValue;
      repairedFields.push(field);
    }
  }

  // Ein Save, der Produkte verloren hatte, darf den Auftrag nicht nebenbei abschließen —
  // ein echtes finishOrder() schreibt immer productsOriginal mit.
  if (repairedFields.includes('products')) {
    const closes = Number(repaired['state']) === 1 && Number(knownRecord['state']) !== 1;
    if (closes) {
      repaired['state'] = knownRecord['state'] ?? 0;
      repairedFields.push('state');
    }
  }

  return { order: repaired as unknown as Order, repairedFields };
};
